import {
  AI_INSIGHTS,
  ALERTS,
  APPROVALS,
  CHURN_RISK_CLIENTS,
  CLIENTS,
  CURRENT_MRR,
  DEPARTMENTS,
  MONTHLY_TARGET,
  OPEN_ROLES,
  TOP_EXPANSION_OPPORTUNITIES,
} from "@/data/mock";
import { formatCurrency, formatPercentValue } from "@/lib/formatters";

export const SUGGESTED_PROMPTS = [
  "What needs my attention today?",
  "Which clients are at risk?",
  "Where are we behind target?",
  "Which leaders need follow-up?",
  "What happened overnight?",
  "What should I discuss in leadership meeting?",
  "Which clients are ready for expansion?",
];

function bullets(lines: string[]) {
  return lines.map((l) => `• ${l}`).join("\n");
}

function attentionSummary(): string {
  const criticalClients = CLIENTS.filter((c) => c.riskLevel === "critical" && c.status !== "churned");
  const openAlerts = ALERTS.filter((a) => a.status === "open");
  const pendingApprovals = APPROVALS.filter((a) => a.status === "pending");
  const overdue = CLIENTS.filter((c) => c.nextFollowUp && new Date(c.nextFollowUp) < new Date());

  return [
    `Here's what needs your attention right now:`,
    bullets([
      `${criticalClients.length} clients at critical risk, representing ${formatCurrency(criticalClients.reduce((s, c) => s + c.mrr, 0) * 12, { compact: true })} in annual revenue.`,
      `${openAlerts.length} open alerts across clients, hiring, and departments.`,
      `${pendingApprovals.length} approvals waiting in your Executive Inbox.`,
      `${overdue.length} client accounts are past their scheduled follow-up date.`,
    ]),
  ].join("\n\n");
}

function atRiskClients(): string {
  const top = CHURN_RISK_CLIENTS.slice(0, 6);
  return [
    `${CHURN_RISK_CLIENTS.length} clients are currently flagged high or critical risk. Top accounts by revenue exposure:`,
    bullets(
      top.map(
        (c) =>
          `${c.name} — health score ${c.healthScore}, ${formatCurrency(c.mrr, { compact: true })}/mo, ${c.openIssues} open issue${c.openIssues === 1 ? "" : "s"} (AM: ${c.assignedAM}).`,
      ),
    ),
  ].join("\n\n");
}

function behindTarget(): string {
  const gap = MONTHLY_TARGET - CURRENT_MRR;
  const laggingDepartments = DEPARTMENTS.filter((d) => d.status === "at-risk" || d.status === "critical");
  return [
    gap > 0
      ? `Current MRR is ${formatCurrency(CURRENT_MRR, { compact: true })} against a target of ${formatCurrency(MONTHLY_TARGET, { compact: true })} — a gap of ${formatCurrency(gap, { compact: true })} (${formatPercentValue((gap / MONTHLY_TARGET) * 100)}).`
      : `Current MRR of ${formatCurrency(CURRENT_MRR, { compact: true })} is ahead of the ${formatCurrency(MONTHLY_TARGET, { compact: true })} target — nice work.`,
    laggingDepartments.length > 0
      ? bullets(laggingDepartments.map((d) => `${d.name} is behind: ${d.aiSummary}`))
      : "All departments are tracking at or above their scorecard targets this cycle.",
  ].join("\n\n");
}

function leadersNeedingFollowUp(): string {
  const tlConcernCounts = new Map<string, number>();
  CLIENTS.filter((c) => c.riskLevel === "critical" || c.riskLevel === "high").forEach((c) => {
    tlConcernCounts.set(c.assignedTL, (tlConcernCounts.get(c.assignedTL) ?? 0) + 1);
  });
  const ranked = [...tlConcernCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

  return [
    "Team leaders with the most at-risk accounts in their book right now:",
    bullets(ranked.map(([name, count]) => `${name} — ${count} at-risk account${count === 1 ? "" : "s"}.`)),
    "Consider a 1:1 coaching check-in with the top names this week.",
  ].join("\n\n");
}

function overnightSummary(): string {
  const recentInsights = [...AI_INSIGHTS].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 4);
  const recentAlerts = [...ALERTS].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 4);

  return [
    "Since your last session:",
    bullets([...recentAlerts.map((a) => `New alert: ${a.category} — ${a.scope}.`), ...recentInsights.map((i) => `New AI insight: ${i.title}.`)]),
  ].join("\n\n");
}

function leadershipMeetingAgenda(): string {
  const strategic = AI_INSIGHTS.filter((i) => i.category === "strategic" || i.category === "department-lag").slice(0, 4);
  const pendingApprovals = APPROVALS.filter((a) => a.status === "pending").length;

  return [
    "Suggested leadership meeting agenda based on this week's signals:",
    bullets([
      ...strategic.map((i) => i.title),
      `${pendingApprovals} pending approvals need executive sign-off.`,
      `${OPEN_ROLES.filter((r) => r.daysOpen > 45).length} roles are open past 45 days and may need a hiring strategy discussion.`,
    ]),
  ].join("\n\n");
}

function expansionReadyClients(): string {
  const top = TOP_EXPANSION_OPPORTUNITIES.slice(0, 6);
  return [
    `${TOP_EXPANSION_OPPORTUNITIES.length} accounts show strong expansion signals (high health + satisfaction with room to grow):`,
    bullets(
      top.map(
        (c) => `${c.name} — health ${c.healthScore}, est. upside ${formatCurrency(c.expansionValue ?? 0, { compact: true })}/mo (AM: ${c.assignedAM}).`,
      ),
    ),
  ].join("\n\n");
}

function genericFallback(): string {
  return [
    "I don't have a scripted analysis for that yet, but here's the current pulse of the business:",
    bullets([
      `${CLIENTS.filter((c) => c.status === "active").length} active clients at ${formatCurrency(CURRENT_MRR, { compact: true })} MRR.`,
      `${ALERTS.filter((a) => a.status === "open").length} open alerts, ${APPROVALS.filter((a) => a.status === "pending").length} pending approvals.`,
    ]),
    "Try asking one of the suggested questions above, or check the relevant page in the sidebar for more detail.",
  ].join("\n\n");
}

const MATCHERS: Array<{ test: (q: string) => boolean; answer: () => string }> = [
  { test: (q) => q.includes("attention"), answer: attentionSummary },
  { test: (q) => q.includes("risk") && q.includes("client"), answer: atRiskClients },
  { test: (q) => q.includes("behind") || q.includes("target"), answer: behindTarget },
  { test: (q) => q.includes("leader") || q.includes("follow-up") || q.includes("follow up"), answer: leadersNeedingFollowUp },
  { test: (q) => q.includes("overnight") || q.includes("happened"), answer: overnightSummary },
  { test: (q) => q.includes("leadership meeting") || q.includes("discuss"), answer: leadershipMeetingAgenda },
  { test: (q) => q.includes("expansion") || (q.includes("ready") && q.includes("client")), answer: expansionReadyClients },
];

export function answerCopilotPrompt(prompt: string): string {
  const q = prompt.toLowerCase();
  const matcher = MATCHERS.find((m) => m.test(q));
  return matcher ? matcher.answer() : genericFallback();
}
